import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import SearchBar from './SearchBar';
import { 
  Menu, 
  Bell, 
  Sun, 
  Settings, 
  LogOut,
  User,
  ChevronDown,
  Calendar,
  Clock
} from 'lucide-react';

const Header = ({ user, onMenuClick }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const emplacement = useLocation();
  const masquerRecherche = user?.role === 'technicien' && ['/ops/tech', '/ops/mon-planning', '/ops/mes-chantiers'].includes(emplacement.pathname);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  
  // État pour les notifications avec statut lu/non lu
  const [notifications, setNotifications] = useState([]);
  // Guetteur nouveaux leads : ids deja vus, bandeau en cours
  const connusRef = React.useRef(null);
  const [toastLead, setToastLead] = useState(null);

  // Moteur audio partage, debloque au premier clic (sonne aussi en arriere-plan)
  const audioCtxRef = React.useRef(null);
  const obtenirCtx = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
    if (audioCtxRef.current.state === 'suspended') { try { audioCtxRef.current.resume(); } catch (e) {} }
    return audioCtxRef.current;
  };

  // Petit "ding" joue via le navigateur (carillon)
  const jouerDing = () => {
    try {
      const ctx = obtenirCtx();
      if (!ctx) return;
      const note = (freq, t0, dur) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'sine';
        o.frequency.value = freq;
        o.connect(g);
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
        g.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + dur);
        o.start(ctx.currentTime + t0);
        o.stop(ctx.currentTime + t0 + dur + 0.05);
      };
      note(659.3, 0, 0.25);
      note(830.6, 0.12, 0.25);
      note(987.8, 0.24, 0.45);
    } catch (e) {}
  };

  const chargerNotifications = React.useCallback(async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const r = await axios.get(`${API}/api/notifications`);
      const liste = (r.data?.data?.notifications || []).map((n) => {
        const ecart = Math.floor((Date.now() - new Date(n.date + 'Z').getTime()) / 60000);
        const temps = ecart < 1 ? "a l'instant"
          : ecart < 60 ? `${ecart}min`
          : ecart < 1440 ? `${Math.floor(ecart / 60)}h`
          : `${Math.floor(ecart / 1440)}j`;
        return {
          id: n.id,
          title: n.titre,
          message: n.message,
          time: temps,
          rawType: n.type,
          type: n.type === 'alerte' ? 'warning' : n.type === 'chantier' ? 'success' : 'info',
          isRead: n.lue,
          url: n.lien || null,
          chantierId: n.type === 'chantier' ? n.lien : null,
          action: null
        };
      });
      setNotifications(liste);

      // Detection des nouveaux leads (son + bandeau), sauf au premier chargement
      const idsActuels = new Set(liste.map((n) => n.id));
      if (connusRef.current === null) {
        connusRef.current = idsActuels;
      } else {
        const nouveaux = liste.filter(
          (n) => !connusRef.current.has(n.id) && !n.isRead && n.rawType === 'lead'
        );
        connusRef.current = idsActuels;
        if (nouveaux.length > 0) {
          jouerDing();
          const premier = nouveaux[0];
          setToastLead(premier);
          if (document.visibilityState !== 'visible' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
            // Commercial sur un autre onglet ou fenetre reduite : notification systeme
            try {
              const sysNotif = new Notification(premier.title, { body: premier.message, tag: premier.id, icon: '/favicon.ico' });
              sysNotif.onclick = () => {
                try { window.focus(); } catch (e) {}
                if (premier.url) { window.location.href = premier.url; }
              };
            } catch (e) {}
          }
        }
      }
    } catch (e) {
      setNotifications([]);
    }
  }, []);

  React.useEffect(() => {
    chargerNotifications();
    const t = setInterval(chargerNotifications, 30000);
    return () => clearInterval(t);
  }, [chargerNotifications]);

  // Fermeture du bandeau 10 s apres qu'il est VISIBLE (attend le retour sur l'onglet)
  useEffect(() => {
    if (!toastLead) return;
    let t = null;
    const demarrer = () => { if (!t) t = setTimeout(() => setToastLead(null), 10000); };
    if (document.visibilityState === 'visible') demarrer();
    const onVis = () => { if (document.visibilityState === 'visible') demarrer(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { if (t) clearTimeout(t); document.removeEventListener('visibilitychange', onVis); };
  }, [toastLead]);

  // Titre de l'onglet : (n) quand il y a du non lu
  const titreBaseRef = React.useRef(null);

  // Demander l'autorisation des notifications systeme au premier clic dans la page
  useEffect(() => {
    const demander = () => {
      try {
        obtenirCtx(); // debloque le moteur audio des le premier clic
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      } catch (e) {}
      document.removeEventListener('click', demander);
    };
    document.addEventListener('click', demander);
    return () => document.removeEventListener('click', demander);
  }, []);

  // Calculer le nombre de notifications non lues
  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  // Mettre a jour le titre de l'onglet selon le non-lu
  useEffect(() => {
    if (titreBaseRef.current === null) titreBaseRef.current = document.title;
    document.title = unreadCount > 0 ? `(${unreadCount}) 🔴 ${titreBaseRef.current}` : titreBaseRef.current;
  }, [unreadCount]);

  // Fonction pour marquer une notification comme lue et naviguer
  const handleNotificationClick = (notification) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      axios.put(`${API}/api/notifications/${notification.id}/lue`).catch(() => {});
    } catch (e) {}

    if (notification.chantierId) {
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
      navigate('/ops/mes-chantiers', { state: { chantierId: notification.chantierId } });
      return;
    }
    // Marquer comme lue
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notification.id 
          ? { ...notif, isRead: true }
          : notif
      )
    );

    // Fermer le dropdown
    setShowNotifications(false);

    // Navigation vers la page liee (fiche du lead, etc.)
    if (notification.url) {
      navigate(notification.url);
    }
  };

  // Fonction pour marquer toutes les notifications comme lues
  const markAllAsRead = () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      axios.put(`${API}/api/notifications/toutes-lues`).catch(() => {});
    } catch (e) {}
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, isRead: true }))
    );
  };

  // Fonction pour mettre à jour l'heure locale française
  const updateLocalTime = () => {
    const now = new Date();
    // Utiliser le fuseau horaire français
    const franceTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Paris"}));
    
    setCurrentTime(franceTime.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit',
      timeZone: 'Europe/Paris'
    }));
    
    setCurrentDate(franceTime.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'Europe/Paris'
    }));
  };

  // Mise à jour en temps réel
  useEffect(() => {
    // Démarrer immédiatement
    updateLocalTime();

    // Mettre à jour toutes les secondes pour afficher l'heure en temps réel
    const interval = setInterval(updateLocalTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const getRoleLabel = (role) => {
    const roles = {
      admin: 'Administrateur',
      commercial: 'Commercial',
      technicien: 'Technicien',
      comptable: 'Comptable',
      depot: 'Dépôt'
    };
    return roles[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800 border-red-200',
      commercial: 'bg-blue-100 text-blue-800 border-blue-200',
      technicien: 'bg-green-100 text-green-800 border-green-200',
      comptable: 'bg-purple-100 text-purple-800 border-purple-200',
      depot: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <header className="relative z-5" style={{ background: 'transparent' }}>
      <style>{`@keyframes clocheAgite { 0%, 60%, 100% { transform: rotate(0); } 66% { transform: rotate(14deg); } 72% { transform: rotate(-12deg); } 78% { transform: rotate(9deg); } 84% { transform: rotate(-6deg); } 90% { transform: rotate(3deg); } }`}</style>
      <div className="flex items-center justify-between">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          {/* Search Bar */}
          <div className="hidden md:flex items-center" style={{ width: '360px' }}>
            {!masquerRecherche && <SearchBar />}
          </div>
        </div>

        {/* Center Section - Date/Time - pilule blanche style Pilot */}
        <div className="hidden lg:flex items-center" style={{ borderRadius: '100px', height: '38px', padding: '0 22px', gap: '10px', background: 'rgba(255,255,255,0.95)', border: '0.5px solid rgba(227,232,238,1)', boxShadow: '0 1px 3px rgba(50,50,93,0.06)', whiteSpace: 'nowrap', boxSizing: 'border-box' }}>
          <span style={{ fontSize: '11px', letterSpacing: '1.2px', color: '#697386', fontWeight: 500, textTransform: 'uppercase' }}>
            {currentDate || "..."}
          </span>
          <span style={{ color: '#697386', opacity: 0.5, fontSize: '13px' }}>·</span>
          <span style={{ fontSize: '11px', letterSpacing: '1.2px', color: '#697386', fontWeight: 500 }}>
            {currentTime || "..:..:"}
          </span>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Notifications avec design moderne et fonctionnalité - visible pour tous */}
          {(
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="flex items-center transition-all duration-200"
                style={{ gap: '8px', height: '38px', padding: '0 14px', borderRadius: '100px', background: 'rgba(255,255,255,0.95)', border: '0.5px solid rgba(227,232,238,1)', boxShadow: '0 1px 3px rgba(50,50,93,0.06)', boxSizing: 'border-box' }}
              >
                <Bell style={{ width: '15px', height: '15px', color: unreadCount > 0 ? '#DC2626' : '#1A1F36', animation: unreadCount > 0 ? 'clocheAgite 1.2s ease-in-out infinite' : 'none', transformOrigin: 'top center' }} />
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center" style={{ minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '100px', background: '#DC2626', color: 'white', fontSize: '10px', fontWeight: 700 }}>{unreadCount}</span>
                )}
              </button>

            {/* Notifications Dropdown Fonctionnel */}
            {showNotifications && (
              <div className="absolute right-0 mt-3 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 py-0 z-50 backdrop-blur-sm">
                {/* Header du dropdown */}
                <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-white text-lg">🔔 Notifications</h3>
                    <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {unreadCount > 0 ? `${unreadCount} nouvelles` : 'Toutes lues'}
                    </span>
                  </div>
                </div>
                
                {/* Liste des notifications */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length > 0 ? notifications.map((notif, index) => (
                    <div 
                      key={notif.id} 
                      onClick={() => handleNotificationClick(notif)}
                      className={`px-6 py-4 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 cursor-pointer border-b border-gray-50 last:border-0 transition-all duration-300 ${
                        !notif.isRead ? 'bg-blue-25 border-l-4 border-l-blue-500' : 'opacity-75'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icône selon le type */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          notif.type === 'info' ? 'bg-blue-100 text-blue-600' :
                          notif.type === 'success' ? 'bg-green-100 text-green-600' :
                          notif.type === 'warning' ? 'bg-orange-100 text-orange-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {notif.rawType === 'lead' ? '🎉' :
                           notif.rawType === 'rappel' ? '📞' :
                           notif.type === 'info' ? '📄' :
                           notif.type === 'success' ? '✅' :
                           notif.type === 'warning' ? '⚠️' : '📌'}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1">
                            <p className={`text-sm font-semibold truncate ${
                              !notif.isRead ? 'text-gray-900' : 'text-gray-600'
                            }`}>
                              {notif.title}
                            </p>
                            <div className="flex items-center gap-2 ml-2">
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                {notif.time}
                              </span>
                              {!notif.isRead && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                              )}
                            </div>
                          </div>
                          <p className={`text-sm leading-relaxed mb-2 ${
                            !notif.isRead ? 'text-gray-600' : 'text-gray-500'
                          }`}>
                            {notif.message}
                          </p>
                          <div className="flex items-center justify-between">
                            <button 
                              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                                notif.type === 'info' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                                notif.type === 'success' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                                notif.type === 'warning' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' :
                                'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {notif.action}
                            </button>
                            {notif.isRead && (
                              <span className="text-xs text-gray-400 italic">✓ Lu</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="px-6 py-8 text-center text-gray-500">
                      <div className="text-4xl mb-2">🔕</div>
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  )}
                </div>
                
                {/* Footer du dropdown */}
                <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <button 
                      onClick={markAllAsRead}
                      className="text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline transition-colors"
                      disabled={unreadCount === 0}
                    >
                      📋 {unreadCount > 0 ? 'Marquer toutes comme lues' : 'Toutes les notifications'}
                    </button>
                    <button 
                      onClick={() => setShowNotifications(false)}
                      className="text-sm text-gray-500 hover:text-gray-700 font-medium px-3 py-1 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
            </div>
          )}

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center transition-all duration-200"
              style={{ gap: '8px', height: '38px', padding: '0 14px 0 5px', borderRadius: '100px', background: 'rgba(255,255,255,0.95)', border: '0.5px solid rgba(227,232,238,1)', boxShadow: '0 1px 3px rgba(50,50,93,0.06)', boxSizing: 'border-box' }}
            >
              <span className="flex items-center justify-center" style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #1FD1FD, #5DDEFF)', color: 'white', fontSize: '12px', fontWeight: 600, flexShrink: 0 }}>
                {(user?.prenom || 'U')[0].toUpperCase()}
              </span>
              <div className="hidden md:block text-left">
                <div style={{ fontSize: '12px', fontWeight: 500, color: '#1A1F36' }}>
                  {user?.prenom} {user?.nom}
                </div>
                <div style={{ fontSize: '9px', color: '#697386', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  {getRoleLabel(user?.role)}
                </div>
              </div>
              <ChevronDown style={{ width: '12px', height: '12px', color: '#697386' }} />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.prenom} {user?.nom}
                  </div>
                  <div className="text-xs text-gray-600">{user?.email}</div>
                </div>
                
                <div className="py-1">
                  <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <User className="w-4 h-4" />
                    Mon profil
                  </button>
                  <button className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    <Settings className="w-4 h-4" />
                    Paramètres
                  </button>
                </div>
                
                <div className="border-t border-gray-100 pt-1">
                  <button
                    onClick={logout}
                    className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Se déconnecter
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bandeau nouveau lead (10 s, clic = ouvrir la fiche) */}
      {toastLead && (
        <div
          onClick={() => { const n = toastLead; setToastLead(null); handleNotificationClick(n); }}
          style={{ position: 'fixed', top: '18px', right: '18px', zIndex: 9999, width: '340px', maxWidth: 'calc(100vw - 36px)', background: '#ffffff', borderRadius: '16px', borderLeft: '5px solid #10b981', boxShadow: '0 16px 44px rgba(15,30,61,0.22)', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 14px', cursor: 'pointer', animation: 'toastLeadIn .3s ease' }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '19px', flexShrink: 0 }}>🎉</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '14px', color: '#0F1E3D' }}>{toastLead.title}</div>
            <div style={{ fontSize: '12.5px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toastLead.message}</div>
          </div>
          <div
            onClick={(e) => { e.stopPropagation(); setToastLead(null); }}
            style={{ color: '#94a3b8', fontSize: '16px', padding: '4px 8px', flexShrink: 0 }}
          >✕</div>
          <style>{`@keyframes toastLeadIn { from { transform: translateY(-12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        </div>
      )}

      {/* Mobile Search */}
      <div className="md:hidden mt-4">
        {!masquerRecherche && <SearchBar />}
      </div>
    </header>
  );
};

export default Header;